import { Component, Inject, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ActivityInterface, ProficiencyLevelEnum } from '@sport-activities/sdk';
import { Subscription } from 'rxjs';

import { appConfig } from '~/app.config';
import { FormCheckboxGroupCheckboxInterface } from '~/component/form/checkbox/group/interfaces/checkbox.interface';
import { FormCheckboxGroupFormControlValueInterface } from '~/component/form/checkbox/group/interfaces/form-control-value.interface';
import { DropdownOption } from '~/component/form/dropdown/interfaces/option.interface';
import { Type } from '~/component/form/input/enums/type.enum';
import { FormGroupValueInterface } from '~/component/form/interfaces/form-group-value.interface';
import { ActivityAgeRangeService } from '~/core/services/activity/age-range/age-range.service';
import { FormControlService } from '~/core/services/form/form-control.service';
import { RoutingInterface } from '~/core/services/navigation/interfaces/routing.interface';
import { NavigationService } from '~/core/services/navigation/navigation.service';
import { TitleService } from '~/core/services/title/title.service';
import { atLeastOneCheckboxCheckedValidator } from '~/core/validators/at-least-one-checkbox-checked/at-least-one-checkbox-checked.validator';
import { rangeValidator } from '~/core/validators/range/range.validator';
import { AbstractOrganizerActivityCreationFormPageDirective } from '~/organizer/activity-creation/components/shared/form-wrapper/classes/abstract-activity-creation-form-page';
import { NAVIGATION_ROUTING } from '~/organizer/shared/components/shared/navigation-buttons/navigation-buttons.component';
import { OrganizerSharedActivityService } from '~/organizer/shared/services/activity/activity.service';
import { OrganizerSharedOrganizationService } from '~/organizer/shared/services/organization/organization.service';
import { ToggleService } from '~/toggles/services/toggle.service';
import { ToggleNameEnum } from '~/toggles/services/toggle/enums/name.enum';

import { AudiencesEnum } from './enums/audiences.enum';

@Component({
  selector: 'app-organizer-activity-creation-audience',
  templateUrl: './audience.component.html',
  styleUrls: [
    '../../../../../organizer/shared/components/shared/form-wrapper/form-wrapper.component.scss',
    './audience.component.scss',
  ],
})
export class OrganizerActivityCreationPageAudienceComponent
  extends AbstractOrganizerActivityCreationFormPageDirective
  implements OnDestroy
{
  protected readonly pageTitleKey =
    'seo.organizer.activity_creation.audience.title';

  public readonly inputNumberType = Type.Number;
  public readonly proficiencyLevels =
    appConfig.formValidationRules.activityProficiencyLevels;

  public audienceOptions: DropdownOption[] = [];
  public audienceSubscription: Subscription;
  public displayValidationError = false;
  public maxAgeInputMax = appConfig.formValidationRules.activityMaxAgeInputMax;
  public maxAgeInputMin = appConfig.formValidationRules.activityMaxAgeInputMin;
  public minAgeInputMax = appConfig.formValidationRules.activityMinAgeInputMax;
  public minAgeInputMin = appConfig.formValidationRules.activityMinAgeInputMin;

  public minAgeInputReadonly = false;
  public proficiencyLevelCheckboxes: FormCheckboxGroupCheckboxInterface[] = [];

  constructor(
    private readonly activityAgeRangeService: ActivityAgeRangeService,
    private readonly translateService: TranslateService,
    protected readonly activatedRoute: ActivatedRoute,
    protected readonly activityService: OrganizerSharedActivityService,
    protected readonly formBuilder: FormBuilder,
    protected readonly navigationService: NavigationService,
    protected readonly organizationService: OrganizerSharedOrganizationService,
    @Inject(NAVIGATION_ROUTING)
    protected readonly navigationRouting: RoutingInterface[],
    protected readonly titleService: TitleService,
    private readonly toggleService: ToggleService,
    private readonly formControlService: FormControlService
  ) {
    super(
      activatedRoute,
      activityService,
      formBuilder,
      navigationService,
      organizationService,
      navigationRouting,
      titleService
    );
  }

  public ngOnDestroy(): void {
    this.audienceSubscription.unsubscribe();
  }

  private get audienceFormControl(): FormControl {
    return this.formGroup.get('audience') as FormControl;
  }

  private get proficiencyLevelAllFormControl(): FormControl {
    return this.formGroup.get('proficiencyLevelAll') as FormControl;
  }

  private get proficiencyLevelsFormArray(): FormArray {
    return this.formGroup.get('proficiencyLevels') as FormArray;
  }

  public get audiences(): { textKey: string; value: string }[] {
    const audience = appConfig.formValidationRules.activityAudience;

    if (this.toggleService.isEnabled(ToggleNameEnum.AudienceAll)) {
      audience.push({
        textKey: 'activity_creation.audience.audience.all.text',
        value: AudiencesEnum.All,
      });
    }

    return audience;
  }

  public get displayProficiencyLevel(): boolean {
    return this.toggleService.isEnabled(
      ToggleNameEnum.ActivityCreationProficiencyLevel
    );
  }

  public get isMaxAgeVisible(): boolean {
    const audience = this.audienceFormControl.value;

    return (
      audience === AudiencesEnum.Children || audience === AudiencesEnum.Custom
    );
  }

  public get isMinAgeVisible(): boolean {
    const audience = this.audienceFormControl.value;

    return (
      audience === AudiencesEnum.Children ||
      audience === AudiencesEnum.Adults ||
      audience === AudiencesEnum.Custom
    );
  }

  public get proficiencyLevelsControls(): FormControl[] {
    return this.proficiencyLevelsFormArray.controls as FormControl[];
  }

  private buildAudienceOptions(): void {
    this.audienceOptions = this.audiences.map(
      (audience): DropdownOption => ({
        disabled: false,
        selected: false,
        text: this.translateService.instant(audience.textKey),
        value: audience.value,
      })
    );
  }

  private buildProficiencyLevelCheckboxes(): void {
    this.proficiencyLevelCheckboxes = this.proficiencyLevels.map(
      (proficiencyLevel) => ({
        key: proficiencyLevel.key,
        label: this.translateService.instant(proficiencyLevel.labelKey),
      })
    );
  }

  /**
   * @example ['level-1', 'level-2', 'level-3'] + [true, false, true] = ['level-1', 'level-3']
   */
  private getProficiencyLevelsSubmitData(
    proficiencyLevelFormGroupValue: FormCheckboxGroupFormControlValueInterface[]
  ): ProficiencyLevelEnum[] {
    return proficiencyLevelFormGroupValue.reduce(
      (
        checkedProficiencyLevels: ProficiencyLevelEnum[],
        proficiencyLevel
      ): ProficiencyLevelEnum[] =>
        proficiencyLevel.checked
          ? [...checkedProficiencyLevels, proficiencyLevel.key]
          : checkedProficiencyLevels,
      []
    );
  }

  private updateAgeFormControls(audienceValue: AudiencesEnum): void {
    const maxAgeControl = this.formGroup.get('maxAge');
    maxAgeControl.reset();

    const minAgeControl = this.formGroup.get('minAge');
    minAgeControl.reset();

    this.minAgeInputReadonly = false;

    const ageRange =
      this.activityAgeRangeService.getAgeRangeFromAudience(audienceValue);

    maxAgeControl.setValue(ageRange.max);
    minAgeControl.setValue(ageRange.min);

    this.updateAgeInputAttributes(ageRange.min, ageRange.max);

    this.formGroup.setValidators(
      rangeValidator('minAge', 'maxAge', ageRange.min, ageRange.max)
    );

    if (ageRange.disabledMin) {
      this.minAgeInputReadonly = true;
    }

    maxAgeControl.updateValueAndValidity();
    minAgeControl.updateValueAndValidity();
  }

  private updateAgeInputAttributes(min, max): void {
    this.maxAgeInputMax = max;
    this.maxAgeInputMin = min;
    this.minAgeInputMax = max;
    this.minAgeInputMin = min;
  }

  protected buildDataBeforeSubmit(
    formGroupValue: FormGroupValueInterface
  ): Partial<ActivityInterface> {
    return {
      typicalAgeRange: {
        max: Number(formGroupValue.maxAge),
        min: Number(formGroupValue.minAge),
      },
      proficiencyLevel: this.displayProficiencyLevel
        ? this.getProficiencyLevelsSubmitData(formGroupValue.proficiencyLevels)
        : [5, 10, 20],
    };
  }

  protected buildFormBuilderOptions(): void {
    const ageRange = this.activityAgeRangeService.getAgeRangeFromAudience(
      this.validationConfig.audience[0]
    );

    this.formBuilderOptions = {
      validators: rangeValidator(
        'minAge',
        'maxAge',
        ageRange.min,
        ageRange.max
      ),
    };
  }

  protected buildValidationConfig(): void {
    const activity = this.activityService.activity;

    // Age range
    const defaultMax =
      activity.typicalAgeRange?.max ||
      this.activityAgeRangeService.defaultMaxAge;
    const defaultMin =
      activity.typicalAgeRange?.min || this.activityAgeRangeService.majorityAge;

    // Audience
    const defaultAudience =
      this.activityAgeRangeService.getAudienceFromAgeRange(
        defaultMin,
        defaultMax
      );

    // Proficiency levels
    const defaultProficiencyLevels = activity.proficiencyLevel || [];
    const proficiencyLevelFormArray = this.proficiencyLevels.map(
      (proficiencyLevel) => ({
        key: proficiencyLevel.key,
        label: this.translateService.instant(proficiencyLevel.labelKey),
        checked: defaultProficiencyLevels.includes(proficiencyLevel.key),
      })
    );

    this.validationConfig = {
      audience: [defaultAudience, Validators.required],
      maxAge: [
        defaultMax,
        [
          Validators.required,
          Validators.pattern('^[0-9]+$'),
          (control: AbstractControl) =>
            Validators.max(this.formGroup ? this.maxAgeInputMax : defaultMax)(
              control
            ),
        ],
      ],
      minAge: [
        defaultMin,
        [
          Validators.required,
          Validators.pattern('^[0-9]+$'),
          (control: AbstractControl) =>
            Validators.max(this.formGroup ? this.minAgeInputMax : defaultMax)(
              control
            ),
        ],
      ],
    };

    if (this.displayProficiencyLevel) {
      this.validationConfig = {
        ...this.validationConfig,
        proficiencyLevelAll:
          defaultProficiencyLevels.length === this.proficiencyLevels.length,
        proficiencyLevels: new FormControl(proficiencyLevelFormArray, {
          validators: atLeastOneCheckboxCheckedValidator(),
        }),
      };
    }
  }

  protected onInitHook(): void {
    this.buildProficiencyLevelCheckboxes();

    this.buildAudienceOptions();

    // Init form
    const ageRange = this.activityAgeRangeService.getAgeRangeFromAudience(
      this.audienceFormControl.value
    );

    this.updateAgeInputAttributes(ageRange.min, ageRange.max);

    if (ageRange.disabledMin) {
      this.minAgeInputReadonly = true;
    }

    // Input changes
    this.audienceSubscription = this.audienceFormControl.valueChanges.subscribe(
      (audienceValue) => {
        this.updateAgeFormControls(audienceValue);
      }
    );
  }

  public changeProficiencyLevelAll(): void {
    const proficiencyLevelAllValue = this.proficiencyLevelAllFormControl.value;
    this.proficiencyLevelsFormArray.setValue(
      this.proficiencyLevelsFormArray.value.map((level) => ({
        ...level,
        checked: proficiencyLevelAllValue,
      }))
    );
  }

  public changeProficiencyLevels(): void {
    this.proficiencyLevelAllFormControl.setValue(
      this.proficiencyLevelsFormArray.value.every(
        (value: FormCheckboxGroupFormControlValueInterface) =>
          value.checked === true
      )
    );
  }

  public errorMessages(inputName: string): string[] {
    const control = this.formGroup.get(inputName);
    const messages = this.formControlService.getErrorMessages(
      control,
      inputName,
      {
        max_age: control.errors?.max?.max || undefined,
      }
    );

    return control?.touched && messages;
  }

  public errorMessagesForLevel(inputName: string): string[] {
    const control = this.formGroup.get(inputName);
    return (
      this.displayValidationError &&
      this.formControlService.getErrorMessages(control, inputName)
    );
  }

  public submit(formGroupValue: FormGroupValueInterface): void {
    if (this.formGroup.invalid) {
      this.displayValidationError = true;
      return;
    }

    super.submit(formGroupValue);
  }
}
